// components/PortalHouseSidebar.jsx

'use client'

import React, { useState, useCallback } from 'react'
import Link from 'next/link'
import {
    FaBriefcase,
    FaChevronLeft,
} from 'react-icons/fa'
import { useMobileMenu } from '@/contexts/MobileMenuContext'
import { useAuth } from '@/contexts/AuthContext'
import { usePathname } from 'next/navigation'
import { houseNavItemsFor } from '@/utils/houseNav'

// ─── Icon base URL ────────────────────────────────────────────────────────────
const ICON_BASE_URL = 'https://fireops.blob.core.windows.net/icons'

// ─── House nav items ──────────────────────────────────────────────────────────
// Same set the house-only sidebar shows below the divider — kept identical so a
// portal user sees the exact same house pages once inside a house.
// Shared definition lives in utils/houseNav.js.

// ─── Blob icon with inline fallback ──────────────────────────────────────────
function HouseNavIcon({ iconPath }) {
    const [error, setError] = useState(false)

    if (error || !iconPath) {
        return (
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                <span className="w-3 h-3 sm:w-4 sm:h-4 block rounded-sm bg-gray-400" />
            </div>
        )
    }

    return (
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
            <img
                src={`${ICON_BASE_URL}/${iconPath}`}
                alt=""
                className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                onError={() => setError(true)}
            />
        </div>
    )
}

// ─── Sidebar component ────────────────────────────────────────────────────────
// Rendered for a full portal user who has drilled into a house from the house
// list (/firehouseadmin). Unlike the house-only sidebar, the top of this menu is
// just "Back" (to the house list) and "House Admin".
export default function PortalHouseSidebar() {
    const { isMobileOpen, closeMobileMenu } = useMobileMenu()
    const { currentOrg, isHouseAdmin } = useAuth()
    const pathname = usePathname()

    const handleNavClick = useCallback(() => {
        closeMobileMenu()
    }, [closeMobileMenu])

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/50 z-30 backdrop-blur-sm"
                    style={{ top: 'var(--header-height)' }}
                    onClick={closeMobileMenu}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar panel */}
            <aside
                className={`
                    fixed left-0
                    w-[280px] sm:w-80 bg-gray-800 text-white
                    flex flex-col shadow-lg z-40
                    transition-transform duration-300 ease-in-out overflow-hidden
                    lg:translate-x-0
                    sidebar-panel
                    ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
                style={{
                    // Height lives in `.sidebar-panel`, NOT here: it needs two
                    // declarations (a vh fallback then dvh) and an inline style can
                    // hold only one value per property. Setting it here would also
                    // beat the class and silently restore the bug — the panel sized
                    // to the LARGE viewport, so its bottom sat under the phone's
                    // browser bar where scrolling could not reach it.
                    top: 'var(--header-height)',
                }}
                role="navigation"
                aria-label="House navigation"
            >
                <nav className="flex-1 overflow-y-auto custom-scrollbar sidebar-scroll">
                    <ul className="py-1 sm:py-2">

                        {/* Back — returns to the house list */}
                        <li>
                            <Link
                                href="/firehouseadmin"
                                onClick={handleNavClick}
                                className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                           text-orange-400 hover:text-orange-300 hover:bg-gray-700
                                           transition-colors text-sm touch-target"
                            >
                                <FaChevronLeft className="text-base sm:text-lg flex-shrink-0" />
                                <span className="truncate font-medium">Back</span>
                            </Link>
                        </li>

                        {/* House Admin — admins of THIS house only; a portal user
                            can browse a house without administering it. */}
                        {isHouseAdmin && (
                        <li>
                            <Link
                                href="/houseadmin"
                                onClick={handleNavClick}
                                className={`
                                    flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                    transition-colors text-sm touch-target font-semibold
                                    ${pathname === '/houseadmin'
                                    ? 'bg-[#b5a472] text-white border-l-4 border-yellow-500'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                }
                                `}
                            >
                                <FaBriefcase className="text-base sm:text-lg flex-shrink-0" />
                                <span className="truncate">House Admin</span>
                            </Link>
                        </li>
                        )}

                        {/* Orange divider */}
                        <li className="px-3 sm:px-4 py-1">
                            <div className="h-[3px] rounded-full bg-orange-500" />
                        </li>

                        {/* Nav items */}
                        {houseNavItemsFor(currentOrg?.orgId).map((item) => {
                            const isActive = pathname === item.href
                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={handleNavClick}
                                        className={`
                                            flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                            transition-colors text-sm touch-target
                                            ${isActive
                                            ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                        }
                                        `}
                                    >
                                        <HouseNavIcon iconPath={item.iconPath} />
                                        <span className="truncate">{item.label}</span>
                                    </Link>
                                </li>
                            )
                        })}

                    </ul>
                </nav>
            </aside>
        </>
    )
}